import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { fromEvent, interval, Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, take, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { PlatformService } from './platform.service';
import { ToastService } from './toast.service';
import * as AuthActions from '../../store/auth/auth.actions';
import * as AuthSelectors from '../../store/auth/auth.selectors';
import { getJwtExpiry } from '../utils/jwt';

// Renew while the token is still valid, so an ordinary call never has to wait
// for a refresh; an already expired token is renewed too, which is what an app
// reopened after days, or a tab left open overnight, comes back with.
const REFRESH_THRESHOLD_MS: number = 30 * 60 * 1000;
const POLL_INTERVAL_MS: number = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private readonly store = inject(Store);
  private readonly apiService = inject(ApiService);
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  private jwtToken: string | null = null;
  private inFlight: Observable<string> | null = null;
  private subscriptions: Subscription | null = null;

  constructor() {
    this.store.select(AuthSelectors.getCurrentJwtToken)
      .subscribe(token => this.jwtToken = token ?? null);
  }

  // Shared while in flight: concurrent callers get one request and one new
  // token, never a rotation race that withdraws the token they just received.
  public refresh(): Observable<string> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const renewing: string | null = this.jwtToken;

    this.inFlight = this.apiService.refreshToken().pipe(
      map(response => {
        if (!response?.Bearer) {
          throw new Error('Refresh returned no bearer');
        }
        return response.Bearer;
      }),
      tap(jwtToken => {
        // Only onto the session it renewed: a logout or another user's login
        // while it was in flight must not get this token.
        if (this.jwtToken === renewing) {
          this.store.dispatch(AuthActions.tokenRefreshed({ jwtToken }));
        }
      }),
      catchError((error: unknown) => {
        // Same guard as above: a refusal of a token the user already left
        // behind must not end the session that replaced it.
        if (renewing !== null && this.jwtToken === renewing) {
          this.onRefreshFailed(error);
        }
        return throwError(() => error);
      }),
      finalize(() => this.inFlight = null),
      shareReplay(1)
    );

    return this.inFlight;
  }

  // A refusal is final: the token was revoked, is past the ceiling, or the
  // password lifetime has run out. Anything else (offline, server hiccup,
  // malformed reply) keeps the session and retries on the next tick.
  private onRefreshFailed(error: unknown): void {
    if (!(error instanceof HttpErrorResponse) || (error.status !== 401 && error.status !== 403)) {
      return;
    }

    this.store.dispatch(AuthActions.logout());

    if (error.error?.Message === 'Password expired') {
      // After the logout's own navigation, so this one wins.
      void this.router.navigate(['auth/reset-expired']);
    }
    else if (error.error?.Message === 'DOMAIN_DISABLED') {
      this.toast.error('LOGIN.DOMAIN_DISABLED');
    }
  }

  public start(): void {
    if (this.subscriptions) {
      return;
    }

    this.subscriptions = new Subscription();

    this.subscriptions.add(
      interval(POLL_INTERVAL_MS).subscribe(() => this.refreshIfNeeded())
    );

    this.subscriptions.add(
      fromEvent(document, 'visibilitychange')
        .pipe(filter(() => document.visibilityState === 'visible'))
        .subscribe(() => this.refreshIfNeeded())
    );

    // On native the WebView is not hidden when the app leaves the foreground,
    // so visibilitychange alone would miss the return from the background.
    this.subscriptions.add(
      this.platformService.appState$
        .pipe(filter(isActive => isActive))
        .subscribe(() => this.refreshIfNeeded())
    );

    this.refreshIfNeeded();
  }

  public stop(): void {
    this.subscriptions?.unsubscribe();
    this.subscriptions = null;
    this.inFlight = null;
  }

  private refreshIfNeeded(): void {
    if (!this.shouldRefresh()) {
      return;
    }

    this.refresh().pipe(take(1)).subscribe({ error: () => undefined });
  }

  private shouldRefresh(): boolean {
    const expiry: number | null = getJwtExpiry(this.jwtToken);

    if (expiry === null) {
      return false;
    }

    return expiry - Date.now() < REFRESH_THRESHOLD_MS;
  }
}
